/**
 * G6/G7/G9 — bag use flow, pure logic. BagScene renders outcomes; this
 * module owns the rules:
 *
 *   - Cartridge blocks no-op uses: a full-HP heal target or a
 *     non-matching cure returns 'no-effect' and the item is NOT
 *     consumed ("It won't have any effect.").
 *   - Consumption gates on runState.removeItem's return value — the
 *     mutation happens only after the effect is known to apply, so a
 *     race (double-tap, stale UI) can never burn an item for nothing.
 *   - Key items are never consumed; use sets FLAG_USED_* and returns
 *     flavor pages.
 *
 * Saving + SFX are scene-lifecycle concerns (same split as script
 * handlers, decision 20) — BagScene calls saveManager/audio on
 * successful outcomes.
 */
import { ITEMS } from '../data/items';
import { runState } from './runState';
import type { PartyMemberStatus } from './runState';

export type UseOutcome =
  | { kind: 'healed'; message: string }
  | { kind: 'cured'; message: string }
  | { kind: 'no-effect'; message: string }
  | { kind: 'key-used'; messages: string[] }
  /** Unknown item, empty count, or missing target — UI should treat as
   *  a silent refresh (state drifted under the panel). */
  | { kind: 'invalid' };

/** Exported for battleBag.ts (G10) — the in-battle mirror of these
 *  rules shares the cartridge strings so the two surfaces never drift. */
export const NO_EFFECT = "It won't have any effect.";

/** Key-item use table: flag set on use + flavor pages. Not consumed. */
const KEY_ITEM_USE: Record<string, { flag: string; messages: string[] }> = {
  town_map: {
    flag: 'FLAG_USED_TOWN_MAP',
    messages: ['You survey the KANTO region.', 'PEWTER CITY is north.'],
  },
  old_rod: {
    flag: 'FLAG_USED_OLD_ROD',
    messages: ['You cast the OLD ROD...', 'Nothing here bites.'],
  },
};

export const CURE_MESSAGE: Record<Exclude<PartyMemberStatus, null>, string> = {
  psn: '{NAME} was cured of its poisoning!',
  tox: '{NAME} was cured of its poisoning!',
  par: '{NAME} was cured of paralysis!',
  brn: "{NAME}'s burn was healed!",
  slp: '{NAME} woke up!',
  frz: '{NAME} was defrosted!',
};

function displayName(species: string): string {
  return species.charAt(0).toUpperCase() + species.slice(1);
}

/** Whether using this item requires picking a party member first. */
export function itemNeedsTarget(itemId: string): boolean {
  const def = ITEMS[itemId];
  return def !== undefined && def.effect.kind !== 'key';
}

/** Use a key item (no target). Sets the FLAG_USED_* bit; item stays. */
export function useKeyItem(itemId: string): UseOutcome {
  const use = KEY_ITEM_USE[itemId];
  if (!use || !runState.hasItem(itemId)) return { kind: 'invalid' };
  runState.setFlag(use.flag);
  return { kind: 'key-used', messages: use.messages };
}

/** Use a heal/cure item on a party member. Consumes only on effect. */
export function useItemOnMember(itemId: string, memberId: string): UseOutcome {
  const def = ITEMS[itemId];
  if (!def || def.effect.kind === 'key' || !runState.hasItem(itemId)) {
    return { kind: 'invalid' };
  }
  const member = runState.getParty().find((m) => m.id === memberId);
  if (!member) return { kind: 'invalid' };

  if (def.effect.kind === 'heal') {
    if (member.hp.current >= member.hp.max) {
      return { kind: 'no-effect', message: NO_EFFECT };
    }
    if (!runState.removeItem(itemId)) return { kind: 'invalid' };
    const healed =
      Math.min(member.hp.max, member.hp.current + def.effect.amount) -
      member.hp.current;
    runState.setMemberHpById(memberId, member.hp.current + healed);
    return {
      kind: 'healed',
      message: `${displayName(member.species)} recovered ${healed} HP!`,
    };
  }

  // cure
  if (!member.status || !def.effect.cures.includes(member.status)) {
    return { kind: 'no-effect', message: NO_EFFECT };
  }
  if (!runState.removeItem(itemId)) return { kind: 'invalid' };
  const message = CURE_MESSAGE[member.status].replace(
    '{NAME}',
    displayName(member.species),
  );
  runState.setMemberStatusById(memberId, null);
  return { kind: 'cured', message };
}
